import {
  CO_BRANDING_SPEC_CODE,
  PRINTED_SIDE_SPEC_CODE,
  QUANTITY_SPEC_CODE,
  S_DESIGN_NOTES,
  S_DESIGN_NOTES_VALUES,
  TURNAROUND_SPEC_CODE,
} from './constants';
import { ElementInfo } from './dom-util';
import {
  CalculatorItem,
  ParsedSku,
  ProductGroup,
  ProductSpecs,
  SpecsHideInCalc,
  State,
} from './types';
import { Calculator } from './calculator';
import { config } from './shopify-checkout-util';
import { PatternSVGRenderer } from './pattern-svg-renderer';

const questionMark = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<rect x="1" y="1" width="22" height="22" rx="11" fill="white"/>
<rect x="1" y="1" width="22" height="22" rx="11" stroke="currentColor" stroke-width="2"/>
<path d="M10.5227 14.0795V13.9318C10.5265 13.2462 10.5909 12.7008 10.7159 12.2955C10.8447 11.8902 11.0265 11.5644 11.2614 11.3182C11.4962 11.0682 11.7822 10.839 12.1193 10.6307C12.3504 10.4867 12.5568 10.3295 12.7386 10.1591C12.9242 9.98485 13.0701 9.79167 13.1761 9.57955C13.2822 9.36364 13.3352 9.12311 13.3352 8.85795C13.3352 8.55871 13.2652 8.29924 13.125 8.07955C12.9848 7.85985 12.7955 7.68939 12.5568 7.56818C12.322 7.44697 12.0587 7.38636 11.767 7.38636C11.4981 7.38636 11.2424 7.44508 11 7.5625C10.7614 7.67614 10.5625 7.85038 10.4034 8.08523C10.2481 8.31629 10.161 8.60985 10.142 8.96591H8.11932C8.13826 8.24621 8.3125 7.64394 8.64205 7.15909C8.97538 6.67424 9.41477 6.31061 9.96023 6.06818C10.5095 5.82576 11.1155 5.70455 11.7784 5.70455C12.5019 5.70455 13.1383 5.83144 13.6875 6.08523C14.2405 6.33902 14.6705 6.70076 14.9773 7.17045C15.2879 7.63636 15.4432 8.18939 15.4432 8.82955C15.4432 9.26136 15.3731 9.64773 15.233 9.98864C15.0966 10.3295 14.9015 10.6326 14.6477 10.8977C14.3939 11.1629 14.0928 11.3996 13.7443 11.608C13.4375 11.7973 13.1856 11.9943 12.9886 12.1989C12.7955 12.4034 12.6515 12.6439 12.5568 12.9205C12.4659 13.1932 12.4186 13.5303 12.4148 13.9318V14.0795H10.5227ZM11.5114 17.625C11.1705 17.625 10.8769 17.5038 10.6307 17.2614C10.3845 17.0189 10.2614 16.7235 10.2614 16.375C10.2614 16.0341 10.3845 15.7424 10.6307 15.5C10.8769 15.2576 11.1705 15.1364 11.5114 15.1364C11.8485 15.1364 12.1402 15.2576 12.3864 15.5C12.6364 15.7424 12.7614 16.0341 12.7614 16.375C12.7614 16.6061 12.7027 16.8163 12.5852 17.0057C12.4716 17.1951 12.3201 17.3466 12.1307 17.4602C11.9451 17.5701 11.7386 17.625 11.5114 17.625Z" fill="black"/>
</svg>
`;

const closeIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
  <path d="M9.5 0.58313C4.66797 0.58313 0.75 4.5011 0.75 9.33313C0.75 14.1652 4.66797 18.0831 9.5 18.0831C14.332 18.0831 18.25 14.1652 18.25 9.33313C18.25 4.5011 14.332 0.58313 9.5 0.58313ZM12.7305 12.6573L11.4414 12.6515L9.5 10.337L7.56055 12.6495L6.26953 12.6554C6.18359 12.6554 6.11328 12.587 6.11328 12.4991C6.11328 12.462 6.12695 12.4269 6.15039 12.3976L8.69141 9.37024L6.15039 6.34485C6.12679 6.31622 6.11369 6.28038 6.11328 6.24329C6.11328 6.15735 6.18359 6.08704 6.26953 6.08704L7.56055 6.0929L9.5 8.40735L11.4395 6.09485L12.7285 6.08899C12.8145 6.08899 12.8848 6.15735 12.8848 6.24524C12.8848 6.28235 12.8711 6.31751 12.8477 6.3468L10.3105 9.37219L12.8496 12.3995C12.873 12.4288 12.8867 12.464 12.8867 12.5011C12.8867 12.587 12.8164 12.6573 12.7305 12.6573Z" fill="black" fill-opacity="0.3"/>
</svg>`;

export class CompactCalculator extends Calculator {
  private groupOpenStatusState: { [groupId: string]: { status: boolean } } = {};

  handleQtyChange(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.syncQty(value);
  }

  handleQtyInput(e: Event) {
    const value = (e.target as HTMLInputElement).value;
    this.syncQty(value);
  }

  updateQty(delta: number) {
    const input = document.getElementById('qty-input') as HTMLInputElement;
    let value = Number(input.value) + delta;
    value = Math.max(1, Math.min(100, value));
    this.syncQty(value);
  }

  findRange(value: number, ranges: any[]) {
    return ranges
      .map((r) => ({ ...r, max: Number(r.code) }))
      .sort((a, b) => a.max - b.max)
      .find((r) => value <= r.max);
  }

  syncQty(value: number | string) {
    const calcItems = this.stateMng.get('calcItems');
    const turnarounds = this.stateMng.get('turnarounds');
    const items = this.stateMng.get('items');
    const turnaroundTotal = turnarounds[calcItems.turnaround]?.cost || 0;

    const total = Object.keys(calcItems.products).reduce((acc, key) => {
      const prod = calcItems.products[key];
      if (!prod.selected) return acc;
      const breakRanges = items
        .find((item) => item.product.code.toUpperCase() === prod.code)
        ?.product.specs.find((spec) => spec.code === QUANTITY_SPEC_CODE)?.specsValue;
      if (breakRanges && breakRanges?.length > 1) {
        const range = this.findRange(Number(value), breakRanges);
        const productTotalPrice =
          Number(value) *
          Math.abs(
            this.getProductPrice(prod).total - Math.abs((range.adderValue * prod.basePrice) / 100),
          );
        calcItems.products[key].qty = Number(value);
        calcItems.products[key].specs[QUANTITY_SPEC_CODE] = {
          adderType: range.adderType,
          adderValue: range.adderValue,
          valueCode: String(value),
        };
        return acc + productTotalPrice;
      } else {
        calcItems.products[key].qty = Number(value);
        calcItems.products[key].specs[QUANTITY_SPEC_CODE] = {
          adderType: 'on_total',
          adderValue: 0,
          valueCode: String(value),
        };

        return acc + Number(value) * this.getProductPrice(prod).total;
      }
    }, turnaroundTotal);

    const oldTotal = this.stateMng.get('total');

    if (oldTotal !== total) {
      if (oldTotal > total) {
        this.stateMng.set('priceStatus', 'DECREASE');
      } else {
        this.stateMng.set('priceStatus', 'INCREASE');
      }
      const comparePricePercentage = this.stateMng.get('comparePricePercentage');
      const comparePrice =
        comparePricePercentage === 0 ? 0 : total / (1 - comparePricePercentage / 100);
      this.stateMng.set('comparePrice', comparePrice);
      this.stateMng.set('total', total);
      this.stateMng.set('calcItems', calcItems);
    }

    (document.getElementById('qty-input') as HTMLInputElement).value = String(value);
  }

  override __renderPriceTableDOMTree(): ElementInfo {
    const items = this.stateMng.get('items');
    const currency = this.stateMng.get('currency');
    const calcItems = this.stateMng.get('calcItems');
    const isQtySwitcherEnabled = this.stateMng.get('enableQuantitySwitcher');

    /**
     * 1️⃣ Collect products that have quantity break specs
     */
    const products = items
      .filter((item) => item.product.specs.some((spec) => spec.code === QUANTITY_SPEC_CODE))
      .map((item) => {
        const calcProd = this.getProductPrice(calcItems.products[item.id]);
        return {
          title: item.product.shortTitle,
          basePrice:
            +calcProd.total +
            (+calcProd.total *
              (item?.product?.productStores.find(
                (store) => store.shopCode === this.stateMng.get('shopCode'),
              )?.multiplierPercentage || 0)) /
              100,
          qtySpec: item.product.specs.find((spec) => spec.code === QUANTITY_SPEC_CODE),
        };
      });

    /**
     * 2️⃣ Collect all unique quantity ranges
     */
    const allQtyRanges = new Set<string>();

    /**
     * 3️⃣ Map:
     * {
     *   "To 1": { "T-Shirt": "100", "Pants": "-" }
     * }
     */
    const tableMap: Record<string, Record<string, string>> = {};

    products.forEach((product) => {
      product.qtySpec?.specsValue?.forEach((spec) => {
        allQtyRanges.add(spec.title);

        const price =
          (product.basePrice + (product.basePrice * spec.adderValue) / 100) * currency.rate;

        const formatted = `${currency.symbol}${price.toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })}`;

        if (!tableMap[spec.title]) {
          tableMap[spec.title] = {};
        }

        tableMap[spec.title][product.title] = formatted;
      });
    });

    /**
     * 4️⃣ Sort quantity ranges numerically
     */
    const sortedRanges = Array.from(allQtyRanges).sort((a, b) => {
      const num = (v: string) => parseInt(v.replace(/[^\d]/g, ''), 10) || 0;
      return num(a) - num(b);
    });

    /**
     * 5️⃣ Build table rows (with sum)
     */
    const rows = sortedRanges.map((range) => {
      let sum = 0;

      const values = products.map((product) => {
        const val = tableMap[range]?.[product.title] ?? '-';

        if (val !== '-') {
          // Remove currency symbol and commas, then convert to number
          const numeric = parseFloat(val.replace(currency.symbol, '').replace(/,/g, ''));
          sum += numeric;
        }

        return val;
      });

      return {
        qty: range,
        values,
        sum: sum
          ? `${currency.symbol}${sum.toLocaleString('en-US', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}`
          : '-',
      };
    });

    /**
     * 6️⃣ Render table
     */
    return {
      tag: 'div',
      attributes: {
        class: 'price-table-small',
        style: !isQtySwitcherEnabled ? 'display: none;' : '',
      },
      children: [
        // HEADER ROW
        {
          tag: 'div',
          attributes: { class: 'price-table-small__row header' },
          children: [
            { tag: 'span', children: ['Quantity'] },
            ...products.map((p) => ({
              tag: 'span',
              children: [p.title],
            })),
            { tag: 'span', children: ['Total'] },
          ],
        },

        // DATA ROWS
        ...rows.map((row) => ({
          tag: 'div',
          attributes: { class: 'price-table-small__row' },
          children: [
            { tag: 'span', children: [row.qty] },
            ...row.values.map((val) => ({
              tag: 'span',
              children: [val],
            })),
            { tag: 'span', children: [row.sum] },
          ],
        })),
      ],
    };
  }

  override getFooterDOMTree(): ElementInfo {
    const isQtySwitcherEnabled = this.stateMng.get('enableQuantitySwitcher');
    const pricePolicyModal = {
      tag: 'div',
      attributes: {
        class: 'calc-footer__privacy-policy__modal',
        id: `price-match-policy`,
        style: 'display:none',
      },
      children: [
        {
          tag: 'div',
          attributes: {
            class: 'calc-footer__privacy-policy__modal__content',
            style:
              'background-color: #F9F6F5;border-bottom-right-radius: 0;border-bottom-left-radius: 0;border-bottom: 1px solid var(--Black-8, #E6E6E6); ',
          },
          children: [
            {
              tag: 'p',
              attributes: {
                class: 'title',
              },
              children: ['Price Match Guarantee'],
            },
          ],
        },
        {
          tag: 'div',
          attributes: {
            class: 'detail',
          },
          children: [
            'We are confident in our prices, but if you find this product with the same specifications at a lower price, simply send us a screenshot of the competitor’s checkout page, and we will gladly match the price for you.',
          ],
        },
      ],
    };
    return {
      tag: 'div',
      attributes: { class: 'calc-footer' },
      children: [
        {
          tag: 'div',
          attributes: { class: 'calc-footer__turnaround' },
          children: [
            {
              tag: 'p',
              attributes: { class: 'calc-footer__turnaround__title' },
              children: [
                'Production Turnaround ',
                { tag: 'span', attributes: { class: 'detail' }, children: [] },
              ],
            },
            {
              tag: 'div',
              attributes: { class: 'calc-footer__turnaround__options' },
              children: this.getTurnaroundDOMTree(),
            },
          ],
        },
        this.__renderPriceTableDOMTree(),
        {
          tag: 'div',
          attributes: {
            class: 'qty-switcher',
            style: !isQtySwitcherEnabled ? 'display: none;' : '',
          },
          children: [
            // +/- buttons
            {
              tag: 'div',
              attributes: { class: 'qty-switcher__controls' },
              children: [
                {
                  tag: 'button',
                  attributes: { class: 'qty-btn', type: 'button' },
                  children: ['−'],
                  events: { click: () => this.updateQty(-1) },
                },
                {
                  tag: 'input',
                  attributes: {
                    type: 'number',
                    value: '1',
                    min: '1',
                    id: 'qty-input',
                  },
                  events: {
                    input: this.handleQtyInput.bind(this),
                  },
                },
                {
                  tag: 'button',
                  attributes: { class: 'qty-btn', type: 'button' },
                  children: ['+'],
                  events: { click: () => this.updateQty(1) },
                },
              ],
            },
          ],
        },

        {
          tag: 'div',
          attributes: { class: 'calc-footer__total' },
          children: [
            { tag: 'span', children: ['Subtotal'] },
            {
              tag: 'div',
              attributes: {
                style: 'display: flex; flex-direction: column; gap: 4px; align-items: end',
              },
              children: [
                {
                  tag: 'span',
                  attributes: {
                    style: 'display: flex; gap: 12px; align-items: center',
                  },
                  children: [
                    {
                      tag: 'span',
                      attributes: { class: 'bundle-compare-at-price', id: 'calc-total-compare-at' },
                      children: [`$0`],
                    },
                    {
                      tag: 'span',
                      attributes: { id: 'calc-total' },
                      children: [`$0`],
                    },
                  ],
                },
                {
                  tag: 'div',
                  attributes: { class: 'bundle-price-match__policy' },
                  children: ['Price Match Guarantee'],
                  events: { click: this.handleShowPricePolicyModal.bind(this) },
                },
              ],
            },
            pricePolicyModal,
          ],
        },
        {
          tag: 'div',
          attributes: { class: 'calc-footer__actions', id: 'calc-footer-actions' },
          children: [''],
        },
      ],
    };
  }

  override showPrice(quantity: number, price: number, selected: boolean): string {
    const currency = this.stateMng.get('currency');

    const finalPrice = (quantity * price * currency.rate).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    return selected ? `${currency.symbol}${finalPrice}` : '';
  }

  expandContainer(productKey: string, isNoneClicked: boolean, groupId = '', isInGroup = false) {
    const targetExpandEl = isInGroup
      ? document.getElementById(`group_${groupId}_prd_${productKey}`)
      : document.getElementById(`spec__expand__${productKey}`);
    if (!targetExpandEl) return;
    if (targetExpandEl?.style.height === '100%') {
      targetExpandEl.style.height = this.getElementHeight(targetExpandEl);
    }
    if (targetExpandEl?.clientHeight && isNoneClicked) {
      setTimeout(() => {
        targetExpandEl.style.height = '0px';
        targetExpandEl.style.overflow = 'hidden'; // Add overflow hidden when the element is closed
      }, 200);
    } else if (targetExpandEl && !isNoneClicked && !targetExpandEl.clientHeight) {
      targetExpandEl.style.height = this.getElementHeight(targetExpandEl) ?? '';
      setTimeout(() => {
        targetExpandEl.style.overflow = 'visible';
      }, 200);
    }
  }

  getElementHeight(expandEl: HTMLElement) {
    return `${Array.from(expandEl.children)
      .map((el) => el.clientHeight)
      .reduce((a, b) => a + b, 0)}px`;
  }

  override __renderCalcItemSelections(calcItems: State['calcItems']): void {
    const isQtySwitcherEnabled = this.stateMng.get('enableQuantitySwitcher');
    Object.keys(calcItems.products).forEach((key) => {
      const product = calcItems.products[key];
      const specEl = document.getElementById('prd_' + key);
      if (specEl) {
        const specRows = specEl.querySelectorAll<HTMLDivElement>('.calc-spec__content__item_specs');
        specRows.forEach((row) => {
          const isControlRow = row.dataset['control'] === 'true';
          const isNoneAdded = row.dataset['isnoneadded'] === 'true';
          const shouldIgnore = row.dataset['ignore'] === 'true';
          const isHidden = row.dataset['visible'] === 'false';
          if (product.selected) {
            for (const specCode in product.specs) {
              const specValue = product.specs[specCode].valueCode;

              const input = specEl.querySelector<HTMLInputElement>(
                `input[type=radio][data-spec="${specCode}"][data-value-code="${specValue}"]`,
              );
              if (input) {
                input.disabled = false;
                input.checked = true;
                const button = input.nextElementSibling as HTMLButtonElement;
                button.removeAttribute('disabled');
              }
            }
            const noneInput = row.querySelector<HTMLInputElement>(
              'input[type=radio][value="NONE"]',
            );
            if (noneInput) noneInput.checked = false;
            if (!isControlRow && !isHidden && !shouldIgnore && isNoneAdded) {
              row.style.display = 'flex';
              this.expandContainer(key, false);
            }
          } else {
            if (!isControlRow && !isHidden && !shouldIgnore && isNoneAdded) {
              this.expandContainer(key, true);
            }
            const specInputs = row.querySelectorAll<HTMLInputElement>('input[type=radio]');
            specInputs.forEach((input) => {
              input.checked = input.value === 'NONE';
            });
          }
        });
      }
      // update price
      const priceEl = document.getElementById('price_prd_' + key);
      if (priceEl && product.specs) {
        const price = this.getProductPrice(product).total;
        const currency = this.stateMng.get('currency');
        const finalPrice = (
          isQtySwitcherEnabled
            ? this.getProductPrice(product).total
            : product.qty * price * currency?.rate
        ).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        priceEl.textContent = product.selected
          ? `${currency?.symbol}${finalPrice} ${isQtySwitcherEnabled ? 'Each' : ''}`
          : '';
      }
    });
  }

  override __renderTurnaround(turnarounds: State['turnarounds']): void {
    if (turnarounds.rush) {
      const rushLabel = document.querySelector<HTMLSpanElement>('#lbl_ta_rush');
      if (rushLabel) {
        const currency = this.stateMng.get('currency');
        rushLabel.className = 'detail';
        rushLabel.textContent = ` ${turnarounds['rush']['period']} Business Days (+ ${currency?.symbol}${(
          turnarounds['rush']['cost'] * currency?.rate
        ).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        })})`;
      }
    }
  }

  protected handleToggleHiddenPrd(items: CalculatorItem[]) {
    const hiddenProducts = this.stateMng.get('visibleHiddenProducts');
    const calcItems = this.stateMng.get('calcItems');
    const shouldExpandPrds = items.filter((item) => !calcItems.products[item?.id]?.selected);
    const hasSelected = items.find((item) => calcItems.products[item?.id]?.selected);
    const expandContainer = document.getElementById('expandContainer');

    if (hasSelected) {
      return;
    }

    if (hiddenProducts.state === 'collapse') {
      items.forEach((item) => {
        const product = document.getElementById('prd_' + item.id);
        if (!product) return;
        product.style.display = 'grid';
      });
      if (expandContainer) expandContainer.style.borderBottom = 'none';
    } else {
      shouldExpandPrds.forEach((item) => {
        const product = document.getElementById('prd_' + item.id);
        if (!product) return;
        product.style.display = 'none';
      });
      if (expandContainer) expandContainer.style.borderBottom = '1px solid rgba(0, 0, 0, 0.05)';
    }

    const collapsedDiv = document.getElementById('collapsedDiv');
    const expandedDiv = document.getElementById('expandedDiv');
    if (collapsedDiv && expandedDiv) {
      if (hiddenProducts.state == 'collapse') {
        expandedDiv.style.display = 'flex';
        collapsedDiv.style.display = 'none';
      } else {
        expandedDiv.style.display = 'none';
        collapsedDiv.style.display = 'flex';
      }
    }

    this.stateMng.set('visibleHiddenProducts', {
      ...hiddenProducts,
      state: hiddenProducts.state == 'expand' ? 'collapse' : 'expand',
    });
  }

  override handleSpecChange(isInGroup: boolean, e: Event) {
    const el = e.target as HTMLSelectElement;
    const productKey = isInGroup
      ? el.closest('.spec-expand__content')?.getAttribute('id')?.split('_prd_')[1]
      : el.closest('.calc-spec')?.getAttribute('id')?.substring(4);
    if (!productKey) {
      console.warn('Product key not found');
      return;
    }

    // 📱 Mobile-specific: also trigger question mark display
    if (window.matchMedia('(hover: none)').matches) {
      const parentItem = el.closest('.calc-spec__content__item');
      if (parentItem) {
        parentItem.classList.add('show-question');

        // Optional: remove after some seconds
        setTimeout(() => {
          parentItem.classList.remove('show-question');
        }, 2000);
      }
    }

    const calcItems = this.stateMng.get('calcItems');
    const qtySwitcherValue = (document.getElementById('qty-input') as HTMLInputElement).value;
    if (el.value === 'NONE') {
      calcItems.products[productKey].selected = false;
    } else if (el.value === 'ENABLE') {
      calcItems.products[productKey].selected = true;
      calcItems.products[productKey].qty = +(qtySwitcherValue || 1);
    } else {
      const specCode = el.dataset['spec'];
      if (!specCode) {
        console.warn('Spec code not found');
        return;
      }

      calcItems.products[productKey].selected = true;
      const value = JSON.parse(el.value);

      if (specCode === QUANTITY_SPEC_CODE) {
        calcItems.products[productKey].qty = +value.code;
      }

      if (specCode === PRINTED_SIDE_SPEC_CODE) {
        if (calcItems?.products?.[productKey]?.specs?.[S_DESIGN_NOTES]) {
          if (value.code === '2') {
            calcItems.products[productKey].specs[S_DESIGN_NOTES] = {
              valueCode: S_DESIGN_NOTES_VALUES[1],
              adderType: 'on_price',
              adderValue: 0,
            };
          } else {
            calcItems.products[productKey].specs[S_DESIGN_NOTES] = {
              valueCode: S_DESIGN_NOTES_VALUES[0],
              adderType: 'on_price',
              adderValue: 0,
            };
          }
        }
      }

      calcItems.products[productKey].specs[specCode] = {
        valueCode: value.code,
        adderType: value.adderType,
        adderValue: value.adderValue,
      };
    }
    const selectedItems = Object.keys(calcItems.products).filter(
      (key) => calcItems.products[key]?.selected,
    );

    const hidden = this.stateMng.get('visibleHiddenProducts').hidden?.map((prd) => prd.id);

    const isHiddenPrdSelected = selectedItems.some((item) => hidden.includes(item));
    const expandedCloseIcon = document.getElementById('collapseIcon');
    if (expandedCloseIcon) {
      if (isHiddenPrdSelected) {
        expandedCloseIcon.style.visibility = 'hidden';
      } else {
        expandedCloseIcon.style.visibility = 'visible';
      }
    }
    this.stateMng.set('calcItems', calcItems);
  }

  override getTurnaroundDOMTree() {
    const turnarounds = this.stateMng.get('turnarounds');
    const turnaround = this.stateMng.get('calcItems').turnaround;
    const turnaroundItems: ElementInfo[] = [
      {
        tag: 'label',
        children: [
          {
            tag: 'input',
            attributes: {
              type: 'radio',
              value: 'reg',
              name: `npt_turnaround`,
              checked: turnaround === 'reg' ? 'true' : undefined,
            },
            events: { change: this.handleTurnaroundChange.bind(this) },
          },
          {
            tag: 'span',
            children: [
              {
                tag: 'span',
                attributes: { class: 'title' },
                children: ['Regular: '],
              },
              {
                tag: 'span',
                attributes: { class: 'detail' },
                children: [`${turnarounds['reg']['period']} Business Days`],
              },
            ],
          },
        ],
      },
    ];

    if (turnarounds?.rush) {
      turnaroundItems.push({
        tag: 'label',
        children: [
          {
            tag: 'input',
            attributes: {
              type: 'radio',
              value: 'rush',
              name: `npt_turnaround`,
              checked: turnaround === 'rush' ? 'true' : undefined,
            },
            events: { change: this.handleTurnaroundChange.bind(this) },
          },
          {
            tag: 'span',
            attributes: {},
            children: [
              {
                tag: 'span',
                attributes: { class: 'title' },
                children: ['Guaranteed Rush: '],
              },
              {
                tag: 'span',
                attributes: { id: 'lbl_ta_rush' },
                children: [
                  'Guaranteed Rush: ',
                  {
                    tag: 'span',
                    attributes: { class: 'detail' },
                    children: [],
                  },
                ],
              },
            ],
          },
        ],
      });
    }

    return turnaroundItems;
  }

  protected handleShowPricePolicyModal(e: Event) {
    const priceMatchPolicy = document.getElementById('price-match-policy');
    if (priceMatchPolicy) {
      if (priceMatchPolicy.style.display === 'none') {
        priceMatchPolicy.style.display = 'block';
      }
      document.addEventListener(
        'click',
        this.handleOutsideClick.bind(this, priceMatchPolicy),
        true,
      );
    }
  }

  protected handleShowDes(e: Event) {
    const el = e.target as HTMLElement;
    if ('target' in el.dataset) {
      const targetKey = el.dataset['target'];
      if (targetKey) {
        const modal = document.getElementById(targetKey);
        const questionMark = document.getElementById(targetKey + '__question');
        if (modal) {
          modal.style.display = 'block';
          if (questionMark) questionMark.style.display = 'flex';
          document.addEventListener('click', this.handleOutsideClick.bind(this, modal), true);
        }
      }
    }
  }

  protected handleOutsideClick(modal: HTMLElement, e: Event) {
    if (!modal.contains(e.target as Node)) {
      const questionMark = document.getElementById(modal.id + '__question');
      modal.style.display = 'none';
      if (questionMark) questionMark.style.display = '';
      document.removeEventListener('click', this.handleOutsideClick.bind(this, modal), true);
    }
  }

  getSpecsDOMTree_(
    prodKey: string,
    specs: ProductSpecs[],
    defaultValues: ParsedSku['specs'],
    hasNoneOption: boolean,
    selected: boolean,
    specsHideInCalc?: SpecsHideInCalc[],
    isInGroup = false,
    isSingleDom = false,
  ): ElementInfo[] {
    let noneRowIndex = 0,
      isNoneAdded = false;

    // show None/Enable buttons only when all of the specs are hidden except Turnaround & hasNoneOption is true
    const showOptionalBtns =
      specs.filter((spec) => spec.code !== 'TA').filter((spec) => !spec.hideInCalc).length == 0 &&
      hasNoneOption;

    const specsItems = specs
      .sort((a: any, b: any) => a.hideInCalc - b.hideInCalc)
      ?.filter((spec) => spec.code !== TURNAROUND_SPEC_CODE)
      ?.map((spec, index) => {
        const overrideSpecHideInCalc = specsHideInCalc?.find(
          (hideSpec) => hideSpec.specCode === spec.code,
        );
        spec.hideInCalc = overrideSpecHideInCalc?.hideInCalc ?? spec.hideInCalc;

        let specValue = defaultValues.find((value) => value.code === spec.code);
        if (!specValue) specValue = { code: spec.code, value: spec.specsValue[0].code };

        if (spec.hideInCalc) {
          noneRowIndex++;
        }

        isNoneAdded = !isNoneAdded && Boolean(hasNoneOption) && index === noneRowIndex;
        const noneSpec: ElementInfo[] = isNoneAdded
          ? [
              {
                tag: 'label',
                children: [
                  {
                    tag: 'input',
                    attributes: {
                      type: 'radio',
                      value: 'NONE',
                      name: 'non_spec_' + spec.key,
                      checked: !selected ? true : undefined,
                    },
                    events: {
                      click: this.handleSpecChange.bind(this, false),
                    },
                  },
                  {
                    tag: 'button',
                    children: ['None'],
                  },
                ],
              },
            ]
          : [];
        const specsDescriptions: ElementInfo = {
          tag: 'div',
          attributes: {
            class: 'calc-spec__content__item__descriptions__modal',
            id: `spec__content__${prodKey}__${spec.code}__descriptions__modal`,
            style: 'display:none',
          },
          children: [
            {
              tag: 'div',
              attributes: {
                class: 'calc-spec__content__item__descriptions__modal__content',
                style:
                  'background-color: #F9F6F5;border-bottom-right-radius: 0;border-bottom-left-radius: 0;border-bottom: 1px solid var(--Black-8, #E6E6E6); ',
              },
              children: [
                {
                  tag: 'p',
                  attributes: {
                    class: 'title',
                  },
                  children: [spec.title || ''],
                },
                {
                  tag: 'p',
                  attributes: { class: 'detail' },
                  children: [spec?.description || ''],
                },
              ],
            },
            {
              tag: 'div',
              attributes: {
                class: 'calc-spec__content__modal',
                style: spec?.specsValue.find((sv) => sv.description) ? undefined : 'display: none;',
              },
              children: [
                ...(spec?.specsValue?.map((specVaue) => {
                  if (specVaue?.hideInCalc) return '';
                  return {
                    tag: 'div',
                    children: [
                      {
                        tag: 'span',
                        attributes: {
                          class: 'calc-spec__content__modal__title',
                          style: specVaue?.description
                            ? 'font-weight: 600; font-size: 12px'
                            : 'display: none',
                        },
                        children: [
                          `${specVaue.title}: `,
                          {
                            tag: 'span',
                            attributes: { class: 'calc-spec__content__modal__desc' },
                            children: [specVaue?.description || ''],
                          },
                        ],
                      },
                    ],
                  };
                }) || []),
              ],
            },
          ],
        };
        const specsQuestionMark = {
          tag: 'div',
          attributes: {
            class: 'calc-spec__content__item__question',
            id: `spec__content__${prodKey}__${spec.code}__descriptions__modal__question`,
            style:
              spec?.description || spec.specsValue?.some((sv) => sv.description)
                ? undefined
                : 'display: none;',
          },
          children: [
            {
              tag: 'img',
              attributes: {
                style: 'color: var(--primary-color);',
                src: URL.createObjectURL(new Blob([questionMark], { type: 'image/svg+xml' })),
                alt: 'Question Mark',
                'data-target': `spec__content__${prodKey}__${spec.code}__descriptions__modal`,
              },
            },
          ],
          events: { click: this.handleShowDes.bind(this) },
        };

        const specItems = {
          tag: 'div',
          attributes: {
            class: 'calc-spec__content__item_specs',
            'data-isNoneAdded': hasNoneOption,
            'data-control': isNoneAdded || undefined,
            'data-visible': spec.hideInCalc ? false : undefined,
            style: spec.hideInCalc || (!selected && !isNoneAdded) ? 'display: none;' : undefined,
          },

          children: [
            ...(showOptionalBtns ? [] : noneSpec),
            ...(spec.specsValue?.map((value) => {
              const isSpecValueHided =
                overrideSpecHideInCalc?.specValues.find(
                  (specValue) => specValue.specValueCode === value.code,
                )?.hideInCalc ?? value.hideInCalc;

              if (specValue?.value) {
                // change the specValue to another value from spec.specsValue when the target spec value hided by admin
                const nonHideInCalcItem = spec.specsValue.find((sv) => !sv.hideInCalc);
                const hideInCalcItem = spec.specsValue.find(
                  (sv) =>
                    sv.hideInCalc ||
                    overrideSpecHideInCalc?.specValues.find(
                      (specValue) => specValue.specValueCode === sv.code,
                    )?.hideInCalc,
                );
                if (hideInCalcItem?.code === specValue.value)
                  specValue = {
                    ...specValue,
                    value: nonHideInCalcItem ? nonHideInCalcItem.code : specValue.code,
                  };
              }
              if (isSpecValueHided) return '';
              return {
                tag: 'label',
                children: [
                  {
                    tag: 'input',
                    attributes: {
                      'data-spec': spec.code,
                      type: 'radio',
                      value: JSON.stringify(value),
                      name: `${prodKey}_spec_` + spec.key,

                      checked: value.code === specValue?.value && selected ? true : undefined,
                      'data-value-code': value.code,
                    },
                    events: {
                      click: this.handleSpecChange.bind(this, isInGroup),
                    },
                  },
                  {
                    tag: 'button',
                    children: [value.title?.trim()],
                  },
                ],
              };
            }) || []),
          ],
        };
        const singleSpecDom = {
          tag: 'div',
          attributes: {
            class: 'calc-spec__header__title',
            style: 'font-size: 15px; font-weight: 600; padding-top: 4px',
          },
          children: [
            spec.hideInCalc || (!selected && !isNoneAdded) ? '' : spec.title,
            {
              tag: 'div',
              attributes: {
                class: 'calc-spec__content__item',
              },
              children: [specItems, specsQuestionMark, specsDescriptions],
            },
          ],
        };

        return isSingleDom
          ? singleSpecDom
          : {
              tag: 'div',
              attributes: {
                class: 'calc-spec__content__item',
              },
              children: [specItems, specsQuestionMark, specsDescriptions],
            };
      });

    const selectionBtns = {
      tag: 'div',
      attributes: {
        class: 'calc-spec__content__item_specs',
        'data-isNoneAdded': hasNoneOption,
        'data-control': isNoneAdded || undefined,
        style: showOptionalBtns ? undefined : 'display: none;',
      },

      children: [
        {
          tag: 'label',
          children: [
            {
              tag: 'input',
              attributes: {
                type: 'radio',
                value: 'NONE',
                name: 'non_spec_' + prodKey,
                checked: !selected ? true : undefined,
              },
              events: {
                click: this.handleSpecChange.bind(this, false),
              },
            },
            {
              tag: 'button',
              children: ['None'],
            },
          ],
        },

        {
          tag: 'label',
          children: [
            {
              tag: 'input',
              attributes: {
                type: 'radio',
                value: 'ENABLE',
                name: 'enable_spec_' + prodKey,
                checked: selected ? true : undefined,
              },
              events: {
                click: this.handleSpecChange.bind(this, false),
              },
            },
            {
              tag: 'button',
              children: ['Add'],
            },
          ],
        },
      ],
    };

    const optionalDom = [
      {
        tag: 'div',
        attributes: {},
        children: [showOptionalBtns ? selectionBtns : specsItems[0]],
      },
      {
        tag: 'div',
        attributes: {
          id: `spec__expand__${prodKey}`,
          style: selected ? 'height: 100%; overflow: visible' : '',
          class: 'spec-expand__content',
        },
        children: showOptionalBtns ? specsItems : specsItems.slice(1),
      },
    ];
    return hasNoneOption ? optionalDom : specsItems;
  }

  private groupProductItemChange(group: ProductGroup, targetItem: CalculatorItem, e: Event) {
    const el = e.target as HTMLSelectElement;
    const calcItems = this.stateMng.get('calcItems');
    const groups = this.stateMng.get('productGroups');
    if (el.value === 'NONE') {
      Object.keys(calcItems.products).forEach((key) => {
        const product = calcItems.products[key];
        if (product.groupId === group.id) {
          product.selected = false;
        }
      });

      // change the groupId of items that have groupId
      groups.forEach((g) => {
        if (g.id === group.id) {
          g.items.forEach((item) => {
            item.selected = false;
          });
        }
      });
      this.stateMng.set('calcItems', calcItems);
    } else {
      // // find group-none input element and chenge its checked to false
      const groupNoneInput = document.getElementById(`group_none__${group.id}`) as HTMLInputElement;

      if (groupNoneInput) {
        groupNoneInput['checked'] = false;
      }
      // make all other calcItems products selected field false
      Object.keys(calcItems.products).forEach((key) => {
        if (calcItems.products[key].groupId === group?.id) calcItems.products[key].selected = false;
      });
      this.stateMng.set('calcItems', calcItems);
      // make all other group items selected field false
      groups.forEach((g) => {
        if (g.id === group.id) {
          g.items.forEach((item) => {
            if (item?.id !== targetItem?.id) {
              item.selected = false;
            } else {
              item.selected = true;
            }
          });
        }
      });
      setTimeout(() => {
        const calcItems = this.stateMng.get('calcItems');
        calcItems.products[targetItem.id].selected = true;
        this.stateMng.set('calcItems', calcItems);
      }, 1);
    }

    this.stateMng.set('productGroups', groups);
  }

  override __renderGroups(): void {
    const groups = this.stateMng.get('productGroups');
    groups.forEach((group) => {
      const groupEl = document.getElementById(`group_${group.id}`) as HTMLInputElement;
      const elProductsRows = groupEl?.querySelectorAll(
        '.group__product__item',
      ) as NodeListOf<HTMLInputElement>;

      const isNoneClicked = !group?.items?.some((item) => item?.selected);
      if (isNoneClicked) {
        if (this.groupOpenStatusState[group.id]) {
          this.groupOpenStatusState[group?.id].status = false;
        } else {
          this.groupOpenStatusState[group?.id] = {
            status: false,
          };
        }
      } else {
        if (this.groupOpenStatusState[group.id]) {
          this.groupOpenStatusState[group?.id].status = true;
        } else {
          this.groupOpenStatusState[group?.id] = {
            status: true,
          };
        }
      }

      elProductsRows?.forEach((row) => {
        if (isNoneClicked) this.expandContainer(row.id.split('_prd_')[1], true, group?.id, true);
        row['checked'] =
          group.items.find((item) => item.id === row.id.split('_ITEM_')[1])?.selected || false;
      });

      const groupPrdContent = groupEl?.querySelectorAll(
        '.spec-expand__content',
      ) as NodeListOf<HTMLInputElement>;

      if (isNoneClicked) {
        groupPrdContent?.forEach((row) => {
          if (row.style.height === '100%') row.style.height = this.getElementHeight(row);
          setTimeout(() => {
            row.style.height = '0px';
            row.style.overflow = 'hidden'; // Add overflow hidden when the element is closed
          }, 200);
        });
      } else {
        groupPrdContent?.forEach((row) => {
          const isSelected = group.items.find(
            (item) => item.id === row.id.split('_prd_')[1],
          )?.selected;
          if (isSelected) this.expandContainer(row.id.split('_prd_')[1], false, group?.id, true);
          if (!isSelected) {
            row.style.height = '0px';
            row.style.overflow = 'hidden';
          }
        });
      }

      const groupPrices = groupEl?.querySelectorAll(
        '.group__price',
      ) as NodeListOf<HTMLInputElement>;
      groupPrices?.forEach((row) => {
        row.style.display = group.items.find((item) => item.id === row.id.split('price_prd_')[1])
          ?.selected
          ? 'inline'
          : 'none';
      });
    });
  }

  override __renderPatternSets(): void {
    const patternSets = this.stateMng.get('patternSets');
    const selectedPatternSet = this.stateMng.get('selectedPatternSet');
    const selectedColorTheme = this.stateMng.get('selectedColorTheme');

    if (!patternSets) return;

    const container = document.getElementById('patternSetsContainer');
    if (!container) return;

    container.innerHTML = '';

    patternSets.forEach(async (pattern) => {
      const item = document.createElement('div');
      item.className = 'pattern-item';
      item.style.cssText = `
      cursor: pointer;
      border: 2px solid transparent;
      border-radius: 6px;
      overflow: hidden;
      width: 60px;
      height: 60px;
      transition: all 0.2s ease;
    `;

      item.dataset['id'] = pattern['id'];
      item.dataset['name'] = pattern['name'];

      const svgUrl =
        config[this.stateMng.get('stage') || 'dev'].ap.storageBucketUrl +
        '/PATTERN_SETS/' +
        pattern.thumbnail;

      const svg = await PatternSVGRenderer.load(svgUrl);

      svg.style.width = '100%';
      svg.style.height = '100%';
      svg.style.display = 'block';

      if (selectedColorTheme) {
        PatternSVGRenderer.applyTheme(svg, selectedColorTheme);
      }

      item.appendChild(svg);

      if (selectedPatternSet?.id === pattern.id) {
        item.style.border = '2px solid #FFC83A';
      }

      item.addEventListener('click', (e) => {
        this.stateMng.set('selectedPatternSet', pattern);
        this.stateMng.set('colorThemes', pattern.colorThemes || []);
        this.stateMng.set('selectedColorTheme', pattern.colorThemes?.[0] || null);

        window.dispatchEvent(new CustomEvent('selectedPatternSet', { detail: pattern }));

        window.dispatchEvent(new CustomEvent('changeSelectedPatternSet', { detail: pattern }));

        container.querySelectorAll('.pattern-item').forEach((el) => {
          (el as HTMLElement).style.border = '2px solid transparent';
        });

        (e.currentTarget as HTMLElement).style.border = '2px solid #FFC83A';
      });

      container.appendChild(item);
    });
  }

  private updateAllPatternThemes(theme: any) {
    document.querySelectorAll<SVGElement>('#patternSetsContainer svg').forEach((svg) => {
      PatternSVGRenderer.updateTheme(svg, theme);
    });
  }

  override __renderColorThemes(): void {
    const colorThemes = this.stateMng.get('colorThemes');
    const selectedColorTheme = this.stateMng.get('selectedColorTheme');

    if (!colorThemes) return;

    const container = document.getElementById('colorThemes');
    if (!container) return;

    container.innerHTML = '';

    // Create a wrapper for theme items
    const themesWrapper = document.createElement('div');
    themesWrapper.style.display = 'flex';
    themesWrapper.style.alignItems = 'center';
    themesWrapper.style.gap = '6px';
    themesWrapper.style.flexWrap = 'wrap';

    // Render existing color themes
    colorThemes.forEach((theme) => {
      const themeItem = document.createElement('div');
      themeItem.className = 'color-theme-item';
      themeItem.style.cssText = `
      display: flex;
      cursor: pointer;
      border: 2px solid transparent;
      border-radius: 600px;
      overflow: hidden;
      width: 30px;
      height: 30px;
      transition: all 0.2s ease;
      background-color: ${theme.primaryColor};
      box-sizing: border-box;
      justify-content: center;
      align-items: center;
      margin-right: 6px;
    `;
      themeItem.dataset['id'] = theme.id;
      themeItem.dataset['name'] = theme.name;

      if (selectedColorTheme && selectedColorTheme.id === theme.id) {
        themeItem.style.borderColor = '#FFC83A';
        themeItem.style.boxShadow = '0 0 0 2px #FFC83A50';
      }

      themeItem.addEventListener('click', (e) => {
        this.stateMng.set('selectedColorTheme', theme);
        this.updateAllPatternThemes(theme);
        window.dispatchEvent(
          new CustomEvent('selectedColorTheme', {
            detail: { ...theme },
          }),
        );

        window.dispatchEvent(
          new CustomEvent('changeSelectedColorTheme', {
            detail: {},
          }),
        );

        themesWrapper.querySelectorAll('.color-theme-item').forEach((el) => {
          (el as HTMLElement).style.borderColor = 'transparent';
          (el as HTMLElement).style.boxShadow = 'none';
        });

        const target = e.currentTarget as HTMLElement;
        target.style.borderColor = '#FFC83A';
        target.style.boxShadow = '0 0 0 2px #FFC83A50';
      });

      themesWrapper.appendChild(themeItem);
    });

    // Create the "+" add button
    const addButton = document.createElement('div');
    addButton.className = 'color-theme-item add-theme';
    addButton.innerHTML = '+';
    addButton.style.cssText = `
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    border: 2px dashed #ccc;
    border-radius: 600px;
    width: 30px;
    height: 30px;
    color: #666;
    font-weight: bold;
    font-size: 20px;
    transition: all 0.2s ease;
  `;

    addButton.addEventListener('mouseenter', () => {
      addButton.style.borderColor = '#FFC83A';
      addButton.style.color = '#FFC83A';
    });

    addButton.addEventListener('mouseleave', () => {
      addButton.style.borderColor = '#ccc';
      addButton.style.color = '#666';
    });

    themesWrapper.appendChild(addButton);
    container.appendChild(themesWrapper);

    // Create inputs container (hidden initially)
    const inputContainer = document.createElement('div');
    inputContainer.style.cssText = `
    display: none;
    gap: 6px;
    flex-wrap: wrap;
  `;

    const inputFields = [
      'primaryColor:#1E88E5',
      'secondaryColor:#43A047',
      'tertiaryColor:#FB8C00',
      'accentColor:#E53935',
    ].map((label) => {
      const input = document.createElement('input');
      input.type = 'color';
      input.value = label.split(':')[1];
      input.placeholder = `${label.split(':')[0]} Color`;
      input.style.cssText = `
      width: 30px;
      height: 30px;
      border: 0.5px solid gray;
      border-radius: 4px;
      cursor: pointer;
      background: #fff;
    `;
      input.dataset['colorType'] = label.split(':')[0];

      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      input.addEventListener('input', () => {
        if (timeoutId) clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          const colors: Record<string, string> = {};
          inputFields.forEach((f) => {
            colors[f.dataset['colorType']!] = f.value;
          });
          this.stateMng.set('customColorTheme', { ...colors });
          this.updateAllPatternThemes({ ...colors });
          window.dispatchEvent(
            new CustomEvent('selectedColorTheme', {
              detail: { ...colors },
            }),
          );
        }, 300);
      });

      return input;
    });

    inputFields.forEach((i) => inputContainer.appendChild(i));

    // Toggle visibility when "+" clicked
    let customMode = false;
    addButton.addEventListener('click', () => {
      customMode = !customMode;
      if (customMode) {
        window.dispatchEvent(
          new CustomEvent('changeSelectedColorTheme', {
            detail: {},
          }),
        );
        window.dispatchEvent(
          new CustomEvent('selectedColorTheme', {
            detail: {
              primaryColor: inputFields[0].value,
              secondaryColor: inputFields[1].value,
              tertiaryColor: inputFields[2].value,
              accentColor: inputFields[3].value,
            },
          }),
        );
        this.stateMng.set('customColorTheme', {
          primaryColor: inputFields[0].value,
          secondaryColor: inputFields[1].value,
          tertiaryColor: inputFields[2].value,
          accentColor: inputFields[3].value,
        });
        this.updateAllPatternThemes({
          primaryColor: inputFields[0].value,
          secondaryColor: inputFields[1].value,
          tertiaryColor: inputFields[2].value,
          accentColor: inputFields[3].value,
        });
      } else {
        this.stateMng.set('customColorTheme', null);
        const selectedColorTheme = this.stateMng.get('selectedColorTheme');
        this.stateMng.set('selectedColorTheme', selectedColorTheme);
        window.dispatchEvent(
          new CustomEvent('changeSelectedColorTheme', {
            detail: {},
          }),
        );
        window.dispatchEvent(
          new CustomEvent('selectedColorTheme', {
            detail: {
              ...selectedColorTheme,
            },
          }),
        );
      }

      if (customMode) {
        inputContainer.insertBefore(addButton, inputContainer.firstChild);
      } else {
        themesWrapper.appendChild(addButton);
      }

      // Toggle display of themes and input fields
      themesWrapper.style.display = customMode ? 'none' : 'flex';
      inputContainer.style.display = customMode ? 'flex' : 'none';
      addButton.innerHTML = customMode ? '−' : '+';
    });
    container.appendChild(inputContainer);
  }

  private getProductGroupDOMTree(group: ProductGroup): ElementInfo {
    const items = group?.items;
    const paramSKUs = this.getParamSKUs();
    const productSKUCounters: Record<string, number> = {};

    const getProductDefaultSpecValue = (productCode: string, counter: number): ParsedSku => {
      const sku = paramSKUs.filter((sku) => sku.code.toLowerCase() === productCode.toLowerCase());
      return sku?.[counter];
    };

    const selected = !!items.find((item) => item.selected);

    return {
      tag: 'div',
      attributes: {
        class: 'calc-spec',
        id: `group_${group.id}`,
      },
      children: [
        {
          tag: 'div',
          attributes: { class: 'calc-group__header__title' },
          children: [
            group?.title,
            ...items.map((item: CalculatorItem) => {
              return {
                tag: 'span',
                attributes: {
                  class: 'group__price',
                  id: `price_prd_${item?.id}`,
                  style: item?.selected ? 'display: inline' : 'display: none',
                },
                children: [],
              };
            }),
          ],
        },
        {
          tag: 'div',
          attributes: { class: 'calc-spec__content__item_specs product-group' },
          children: [
            {
              tag: 'label',
              children: [
                {
                  tag: 'input',
                  attributes: {
                    id: `group_none__${group.id}`,
                    type: 'radio',
                    value: 'NONE',
                    checked: !selected ? true : undefined,
                  },
                  events: {
                    change: this.groupProductItemChange.bind(this, group, group.items[0]),
                  },
                },
                {
                  tag: 'button',
                  children: ['None'],
                },
              ],
            },
            ...items.map((item) => {
              return {
                tag: 'label',
                attributes: {
                  class: 'group__product__item',
                },
                children: [
                  {
                    tag: 'input',
                    attributes: {
                      class: 'group__product__item',
                      type: 'radio',
                      name: 'group_product',
                      value: group.id,
                      id: group.id + '_ITEM_' + item.id,
                      checked: item.selected ? 'true' : undefined,
                    },
                    events: {
                      change: this.groupProductItemChange.bind(this, group, item),
                    },
                  },
                  {
                    tag: 'button',
                    children: [item?.title],
                  },
                ],
              };
            }),
          ],
        },
        ...items.map((item: CalculatorItem) => {
          productSKUCounters[item.product.code] =
            productSKUCounters[item.product.code] === 0
              ? productSKUCounters[item.product.code] + 1
              : 0;

          const defaultSpecs =
            getProductDefaultSpecValue(item.product.code, productSKUCounters[item.product.code])
              ?.specs || item.parsedSku.specs;
          return {
            tag: 'div',
            attributes: {
              class: 'spec-expand__content',
              id: `group_${group.id}_prd_${item.id}`,
              style: item?.selected
                ? 'height: 100%; overflow: visible'
                : 'height: 0px; overflow: hidden',
            },
            children: this.getSpecsDOMTree_(
              item.id,
              item.product.specs || [],
              defaultSpecs,
              false,
              true,
              item.specsHideInCalc,
              true,
            ),
          };
        }),
      ],
    };
  }

  private getCompactDOMTree(items: CalculatorItem[]): ElementInfo[] {
    const paramSKUs = this.getParamSKUs();
    const productSKUCounters: Record<string, number> = {};

    const getProductDefaultSpecValue = (productCode: string, counter: number): ParsedSku => {
      const sku = paramSKUs.filter((sku) => sku.code.toLowerCase() === productCode.toLowerCase());
      return sku?.[counter];
    };

    const isSingle = items?.length === 1;

    return items
      ?.filter((item) => !item.productGroupId)
      .map((item) => {
        productSKUCounters[item.product.code] =
          productSKUCounters[item.product.code] === 0
            ? productSKUCounters[item.product.code] + 1
            : 0;

        const defaultSpecs =
          getProductDefaultSpecValue(item.product.code, productSKUCounters[item.product.code])
            ?.specs || item.parsedSku.specs;

        return {
          tag: 'div',
          attributes: {
            class: `calc-spec ${item.product.specs?.filter((spec) => spec.code !== TURNAROUND_SPEC_CODE).length ? '' : 'empty-specs'}`,
            id: `prd_${item.id}`,
            style: item?.isHide ? 'display: none' : '',
          },
          children: [
            !isSingle
              ? {
                  tag: 'div',
                  attributes: { class: `calc-spec__header` },
                  children: [
                    {
                      tag: 'div',
                      attributes: { class: 'calc-spec__header__title' },
                      children: [
                        item.product.shortTitle,
                        {
                          tag: 'span',
                          attributes: {
                            class: '',
                            id: `tmpl_url_${item.id}`,
                          },
                          children: [],
                        },
                      ],
                    },
                    {
                      tag: 'span',
                      attributes: {
                        class: 'calc-spec__header__title__price',
                        id: `price_prd_${item.id}`,
                      },
                      children: [],
                    },
                  ],
                }
              : '',
            {
              tag: 'div',
              attributes: { class: 'calc-spec__content' },
              children: this.getSpecsDOMTree_(
                item.id,
                item.product.specs || [],
                defaultSpecs,
                item.optional,
                item.selected,
                item.specsHideInCalc,
                false,
                isSingle,
              ),
            },
          ],
        };
      });
  }

  override getSingleDOMTree(item: CalculatorItem): ElementInfo[] {
    const defaultSpecParams = this.getParamSKUs();
    const skuFromParam = defaultSpecParams.find(
      (sku) => sku.code.toLowerCase() === item.product.code.toLowerCase(),
    );
    // set proper template url id
    const tmplUrl = document.getElementById('tmpl_url_single_pdp');
    if (tmplUrl) tmplUrl.id = `tmpl_url_${item.id}`;

    const isApparel = this.stateMng.get('isApparel');

    const thumbnailsDom = isApparel
      ? [
          {
            tag: 'div',
            attributes: {
              style:
                'display: flex; gap: 8px; flex-wrap: wrap; padding: 8px; border-bottom: 1px solid rgba(0,0,0,.05);',
              id: 'patternSetsContainer',
            },
          },
          {
            tag: 'div',
            attributes: {
              style:
                'display: flex; gap: 8px; flex-wrap: wrap; padding: 8px; border-bottom: 1px solid rgba(0,0,0,.05);',
              id: 'colorThemes',
            },
          },
        ]
      : [];

    return [
      {
        tag: 'div',
        attributes: {
          class: `calc-spec`,
          id: `prd_${item.id}`,
        },
        children: [
          {
            tag: 'div',
            attributes: { class: 'calc-spec__content' },
            children: [
              ...thumbnailsDom,
              ...this.getSpecsDOMTree_(
                item.id,
                item.product.specs,
                skuFromParam?.specs || item.parsedSku.specs,
                item.optional,
                item.selected,
                undefined,
                false,
                true,
              ),
            ],
          },
        ],
      },
    ];
  }

  override getBundleDOMTree(items: CalculatorItem[]): ElementInfo[] {
    const groups = this.stateMng.get('productGroups') || [];

    const hiddenProducts = items.filter((item) => item.isHide);
    const nonHideProducts = items.filter((item) => !item.isHide);
    const isApparel = this.stateMng.get('isApparel');
    const thumbnailsDom = isApparel
      ? [
          {
            tag: 'div',
            attributes: {
              style:
                'display: flex; gap: 8px; flex-wrap: wrap; padding: 8px; border-bottom: 1px solid rgba(0,0,0,.05);',
              id: 'patternSetsContainer',
            },
          },
          {
            tag: 'div',
            attributes: {
              style:
                'display: flex; gap: 8px; flex-wrap: wrap; padding: 8px; border-bottom: 1px solid rgba(0,0,0,.05);',
              id: 'colorThemes',
            },
          },
        ]
      : [];
    return [
      ...thumbnailsDom,
      ...this.getCompactDOMTree(nonHideProducts),
      ...groups.map((g) => this.getProductGroupDOMTree(g)),
      ...(hiddenProducts.length > 0
        ? [
            {
              tag: 'div',
              attributes: {
                class: 'expand__container',
                id: `expandContainer`,
              },
              children: [
                {
                  tag: 'div',
                  attributes: {
                    id: 'collapsedDiv',
                  },
                  children: [
                    {
                      tag: 'p',
                      attributes: {},
                      children: ['Looking for even more options?'],
                    },
                    {
                      tag: 'button',
                      attributes: {
                        id: '',
                      },
                      events: {
                        click: this.handleToggleHiddenPrd.bind(this, hiddenProducts),
                      },
                      children: ['View More Accessories'],
                    },
                  ],
                },
                {
                  tag: 'div',
                  attributes: {
                    id: 'expandedDiv',
                    style: 'display: none;',
                  },
                  children: [
                    {
                      tag: 'p',
                      attributes: {
                        style: 'visibility: hidden;',
                      },
                      children: [`-`],
                    },
                    {
                      tag: 'p',
                      children: ['More Accessories'],
                    },
                    {
                      tag: 'img',
                      attributes: {
                        style: 'color: var(--primary-color);',
                        src: URL.createObjectURL(new Blob([closeIcon], { type: 'image/svg+xml' })),
                        alt: 'Close',
                        id: 'collapseIcon',
                      },
                      events: {
                        click: this.handleToggleHiddenPrd.bind(this, hiddenProducts),
                      },
                    },
                  ],
                },
              ],
            },
            ...this.getCompactDOMTree(hiddenProducts),
          ]
        : []),
    ];
  }
}

if (typeof window !== 'undefined') {
  window['CompactCalculator'] = CompactCalculator;
}
